<template>
  <v-container class="py-6" max-width="1200">
    <!-- Header -->
    <div class="d-flex align-center mb-1">
      <v-btn icon variant="text" to="/admin" size="small" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon color="success" size="28" class="mr-3">mdi-credit-card-outline</v-icon>
      <h1 class="text-h5 font-weight-bold">Abonnementer</h1>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-6 ml-12">Overblik over alle abonnementer og betalinger</p>

    <!-- Summary Cards -->
    <v-row class="mb-6">
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="success" size="28" class="mb-1">mdi-check-circle</v-icon>
            <div class="text-h5 font-weight-bold">{{ summaryStats.active }}</div>
            <div class="text-caption text-medium-emphasis">Aktive</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="info" size="28" class="mb-1">mdi-clock-outline</v-icon>
            <div class="text-h5 font-weight-bold">{{ summaryStats.trialing }}</div>
            <div class="text-caption text-medium-emphasis">Prøveperiode</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="warning" size="28" class="mb-1">mdi-alert-circle</v-icon>
            <div class="text-h5 font-weight-bold">{{ summaryStats.pastDue }}</div>
            <div class="text-caption text-medium-emphasis">Forfaldne</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="error" size="28" class="mb-1">mdi-cancel</v-icon>
            <div class="text-h5 font-weight-bold">{{ summaryStats.canceled }}</div>
            <div class="text-caption text-medium-emphasis">Annullerede</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filter -->
    <v-card variant="outlined" class="mb-4">
      <v-card-text class="d-flex align-center ga-3">
        <v-select
          v-model="filterStatus"
          :items="statusOptions"
          label="Status"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 200px"
          @update:model-value="fetchSubscriptions"
        />
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Søg efter bruger..."
          density="compact"
          variant="outlined"
          hide-details
          clearable
          style="max-width: 300px"
          @update:model-value="debouncedFetch"
        />
      </v-card-text>
    </v-card>

    <!-- Subscriptions Table -->
    <v-card variant="outlined">
      <v-table v-if="subscriptions.length" density="comfortable" hover>
        <thead>
          <tr>
            <th>Bruger</th>
            <th>Plan</th>
            <th>Status</th>
            <th>Startdato</th>
            <th>Næste betaling</th>
            <th class="text-right">Handlinger</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sub in subscriptions" :key="sub.id">
            <td>
              <div>
                <span class="font-weight-medium">{{ sub.user?.displayName || sub.user?.username }}</span>
                <div class="text-caption text-medium-emphasis">{{ sub.user?.email }}</div>
              </div>
            </td>
            <td>{{ sub.planName || 'Standard' }}</td>
            <td>
              <v-chip size="small" :color="statusColor(sub.status)" variant="tonal">
                {{ statusLabel(sub.status) }}
              </v-chip>
            </td>
            <td>{{ formatDate(sub.startDate) }}</td>
            <td>{{ formatDate(sub.currentPeriodEnd) }}</td>
            <td class="text-right">
              <v-btn
                v-if="sub.status === 'active' || sub.status === 'trialing'"
                size="small"
                variant="text"
                color="error"
                @click="confirmCancel(sub)"
              >
                Annuller
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-card-text v-else-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </v-card-text>

      <v-card-text v-else class="text-center text-medium-emphasis py-12">
        <v-icon size="48" class="mb-3">mdi-credit-card-off</v-icon>
        <p>Ingen abonnementer fundet. API-endepunkterne skal implementeres.</p>
      </v-card-text>
    </v-card>

    <!-- Cancel Confirmation -->
    <v-dialog v-model="cancelDialog" max-width="400">
      <v-card>
        <v-card-title class="text-error d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          Annuller abonnement
        </v-card-title>
        <v-card-text>
          Er du sikker på, at du vil annullere abonnementet for
          <strong>{{ cancelTarget?.user?.displayName || cancelTarget?.user?.username }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelDialog = false">Nej</v-btn>
          <v-btn variant="flat" color="error" :loading="canceling" @click="doCancel">
            Ja, annuller
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { getSubscriptions, cancelSubscription } from "../../services/adminApi";

const subscriptions = ref([]);
const loading = ref(false);
const search = ref("");
const filterStatus = ref("all");

const summaryStats = reactive({ active: "–", trialing: "–", pastDue: "–", canceled: "–" });

const statusOptions = [
  { title: "Alle", value: "all" },
  { title: "Aktiv", value: "active" },
  { title: "Prøveperiode", value: "trialing" },
  { title: "Forfalden", value: "past_due" },
  { title: "Annulleret", value: "canceled" },
];

const cancelDialog = ref(false);
const cancelTarget = ref(null);
const canceling = ref(false);

let debounceTimer = null;
function debouncedFetch() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fetchSubscriptions, 300);
}

async function fetchSubscriptions() {
  loading.value = true;
  try {
    const params = {};
    if (filterStatus.value !== "all") params.status = filterStatus.value;
    if (search.value) params.search = search.value;

    const { data } = await getSubscriptions(params);
    subscriptions.value = data.subscriptions || [];

    if (data.summary) {
      summaryStats.active = data.summary.active ?? "–";
      summaryStats.trialing = data.summary.trialing ?? "–";
      summaryStats.pastDue = data.summary.pastDue ?? "–";
      summaryStats.canceled = data.summary.canceled ?? "–";
    }
  } catch {
    // API not implemented yet
  }
  loading.value = false;
}

function statusColor(status) {
  if (status === "active") return "success";
  if (status === "trialing") return "info";
  if (status === "past_due") return "warning";
  if (status === "canceled") return "error";
  return "grey";
}

function statusLabel(status) {
  if (status === "active") return "Aktiv";
  if (status === "trialing") return "Prøve";
  if (status === "past_due") return "Forfalden";
  if (status === "canceled") return "Annulleret";
  return status;
}

function formatDate(dateStr) {
  if (!dateStr) return "–";
  return new Date(dateStr).toLocaleDateString("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function confirmCancel(sub) {
  cancelTarget.value = sub;
  cancelDialog.value = true;
}

async function doCancel() {
  canceling.value = true;
  try {
    await cancelSubscription(cancelTarget.value.id);
    cancelDialog.value = false;
    await fetchSubscriptions();
  } catch (err) {
    alert("Fejl: " + (err.response?.data?.error || err.message));
  }
  canceling.value = false;
}

onMounted(fetchSubscriptions);
</script>